<script setup>
import { computed, ref } from 'vue';
import PlatformBadge from './PlatformBadge.vue';
import { roleLabel } from '../utils/politicalRoles.js';

const props = defineProps({
  competitors: { type: Array, default: () => [] },
});

const COLUMNS = [
  { key: 'name', label: 'Perfil', align: 'left' },
  { key: 'platform', label: 'Plat.', align: 'left' },
  { key: 'shareOfVoice', label: 'SoV %', align: 'right' },
  { key: 'sentimentNet', label: 'Sent. Net', align: 'center' },
  { key: 'dominantTopics', label: 'Temas', align: 'left' },
  { key: 'postsPerDay', label: 'Posts/d', align: 'right' },
  { key: 'engagementPerPost', label: 'Eng/post', align: 'right' },
  { key: 'growthPct', label: 'Crescim.', align: 'right' },
];

const sortKey = ref('shareOfVoice');
const sortDir = ref('desc');

const sorted = computed(() => {
  const list = [...props.competitors];
  list.sort((a, b) => {
    const av = a[sortKey.value];
    const bv = b[sortKey.value];
    if (typeof av === 'string') {
      return sortDir.value === 'desc'
        ? bv.localeCompare(av)
        : av.localeCompare(bv);
    }
    return sortDir.value === 'desc' ? (bv || 0) - (av || 0) : (av || 0) - (bv || 0);
  });
  return list;
});

const setSort = key => {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'desc' ? 'asc' : 'desc';
  } else {
    sortKey.value = key;
    sortDir.value = 'desc';
  }
};

const sentimentNetWidth = net => {
  // net is -100 to +100; bar 100% wide; center at 50%
  const abs = Math.min(50, Math.abs(net) / 2);
  return abs;
};
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-background p-4">
    <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
      Benchmark Competitivo
    </h3>

    <div v-if="!competitors.length" class="py-6 text-center text-sm text-n-slate-11">
      Sem perfis cadastrados.
    </div>

    <div v-else class="overflow-x-auto">
      <table class="w-full text-xs">
        <thead>
          <tr class="border-b border-n-weak text-[10px] uppercase tracking-wide text-n-slate-11">
            <th
              v-for="col in COLUMNS"
              :key="col.key"
              class="cursor-pointer select-none py-2 pr-2 hover:text-n-slate-12"
              :class="{
                'text-left': col.align === 'left',
                'text-right': col.align === 'right',
                'text-center': col.align === 'center',
              }"
              @click="setSort(col.key)"
            >
              {{ col.label }}
              <span v-if="sortKey === col.key" class="ml-0.5">
                {{ sortDir === 'desc' ? '↓' : '↑' }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="c in sorted"
            :key="c.id"
            class="border-b border-n-weak last:border-none hover:bg-n-slate-2"
          >
            <td class="py-2 pr-2">
              <div class="flex flex-col gap-0.5">
                <span class="font-semibold text-n-slate-12">{{ c.name }}</span>
                <div class="flex items-center gap-1.5 text-[10px] text-n-slate-11">
                  <span v-if="c.type === 'target'" class="rounded bg-blue-100 px-1 py-px font-semibold text-blue-700">
                    Alvo
                  </span>
                  <span v-if="c.politicalRole">
                    {{ roleLabel(c.politicalRole) }}
                  </span>
                </div>
              </div>
            </td>
            <td class="py-2 pr-2"><PlatformBadge :platform="c.platform" size="xs" /></td>
            <td class="py-2 pr-2 text-right">
              <span class="font-semibold text-n-slate-12">{{ c.shareOfVoice || 0 }}%</span>
              <div class="mt-1 h-1 w-full overflow-hidden rounded-full bg-n-slate-3">
                <div class="h-full rounded-full bg-blue-500" :style="{ width: `${c.shareOfVoice || 0}%` }" />
              </div>
            </td>
            <td class="py-2 pr-2 text-center">
              <div class="relative mx-auto h-2 w-20 overflow-hidden rounded-full bg-n-slate-3">
                <div class="absolute left-1/2 top-0 h-full w-px bg-n-slate-11/40" />
                <div
                  v-if="c.sentimentNet >= 0"
                  class="absolute left-1/2 top-0 h-full bg-emerald-500"
                  :style="{ width: `${sentimentNetWidth(c.sentimentNet)}%` }"
                />
                <div
                  v-else
                  class="absolute right-1/2 top-0 h-full bg-rose-500"
                  :style="{ width: `${sentimentNetWidth(c.sentimentNet)}%` }"
                />
              </div>
              <span
                class="mt-0.5 block text-[10px] font-semibold"
                :class="c.sentimentNet > 0 ? 'text-emerald-700' : c.sentimentNet < 0 ? 'text-rose-700' : 'text-n-slate-11'"
              >
                {{ c.sentimentNet > 0 ? '+' : '' }}{{ c.sentimentNet || 0 }}
              </span>
            </td>
            <td class="py-2 pr-2">
              <div class="flex flex-wrap gap-1">
                <span
                  v-for="(t, i) in c.dominantTopics || []"
                  :key="`t-${c.id}-${i}`"
                  class="rounded bg-n-slate-3 px-1.5 py-0.5 text-[10px] text-n-slate-12"
                >
                  {{ t }}
                </span>
                <span v-if="!c.dominantTopics?.length" class="text-[10px] text-n-slate-11">
                  —
                </span>
              </div>
            </td>
            <td class="py-2 pr-2 text-right text-n-slate-12">{{ c.postsPerDay || 0 }}</td>
            <td class="py-2 pr-2 text-right text-n-slate-12">{{ c.engagementPerPost || 0 }}</td>
            <td
              class="py-2 pr-2 text-right font-semibold"
              :class="(c.growthPct || 0) > 0 ? 'text-emerald-700' : (c.growthPct || 0) < 0 ? 'text-rose-700' : 'text-n-slate-11'"
            >
              {{ (c.growthPct || 0) > 0 ? '+' : '' }}{{ c.growthPct || 0 }}%
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
